/**
 * 日志服务配置
 *
 * 模块说明：
 * 统一管理日志服务的 API 地址，支持：
 * 1. 使用当前后端 API（默认）
 * 2. 切换到独立的第三方日志服务器
 * 3. 通过环境变量或远程配置动态切换
 *
 * 使用方法：
 * 1. 默认使用后端 API：无需配置
 * 2. 使用独立日志服务器：设置环境变量 VITE_LOG_SERVICE_URL
 * 3. 动态切换：调用 setLogServiceUrl() 或从远程配置获取
 *
 * @author YSFL Team
 * @date 2026-01-29
 */

import { useAccessStore } from '@vben/stores';

import { resolveApiUrl } from '#/api/runtime-config';
import { getDeviceId } from '#/utils/device';

// ========================================================================
// 配置
// ========================================================================

/** 日志服务配置 */
interface LogServiceConfig {
  /** 日志服务基础 URL（空字符串表示使用当前域名） */
  baseUrl: string;
  /** 错误日志端点 */
  errorEndpoint: string;
  /** 性能日志端点 */
  performanceEndpoint: string;
  /** 行为日志端点 */
  behaviorEndpoint: string;
}

/** 默认配置：使用当前后端 API */
const DEFAULT_CONFIG: LogServiceConfig = {
  baseUrl: '', // 空字符串 = 使用相对路径，由 Nginx 代理
  errorEndpoint: '/api/v1/web-logs/errors',
  performanceEndpoint: '/api/v1/web-logs/performance',
  behaviorEndpoint: '/api/v1/web-logs/behavior',
};

/** 当前配置 */
const currentConfig: LogServiceConfig = { ...DEFAULT_CONFIG };

const RATE_LIMIT_WINDOW_MS = 60_000;
const MAX_REQUESTS_PER_WINDOW = 120;
const RATE_LIMIT_COOLDOWN_MS = 60_000;
const requestTimestamps: number[] = [];
let cooldownUntil = 0;

function canSendLogRequest(): boolean {
  const now = Date.now();

  if (now < cooldownUntil) {
    return false;
  }

  while (
    requestTimestamps.length > 0 &&
    requestTimestamps[0] !== undefined &&
    requestTimestamps[0] <= now - RATE_LIMIT_WINDOW_MS
  ) {
    requestTimestamps.shift();
  }

  if (requestTimestamps.length >= MAX_REQUESTS_PER_WINDOW) {
    return false;
  }

  requestTimestamps.push(now);
  return true;
}

function markLogRequestLimited(retryAfterHeader: null | string): void {
  const retryAfterSeconds = Number(retryAfterHeader);
  cooldownUntil =
    Date.now() +
    (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? retryAfterSeconds * 1000
      : RATE_LIMIT_COOLDOWN_MS);
}

// ========================================================================
// 初始化
// ========================================================================

/**
 * 初始化日志服务配置
 *
 * 优先级：
 * 1. 环境变量 VITE_LOG_SERVICE_URL（构建时确定）
 * 2. 远程配置（运行时动态）
 * 3. 默认配置（使用后端 API）
 */
export function initLogServiceConfig(): void {
  // 从环境变量读取日志服务 URL
  const envUrl = import.meta.env.VITE_LOG_SERVICE_URL;

  if (envUrl) {
    currentConfig.baseUrl = envUrl;
    // eslint-disable-next-line no-console
    console.info('[LogService] 使用环境变量配置的日志服务:', envUrl);
  }
}

// ========================================================================
// 配置管理
// ========================================================================

/**
 * 设置日志服务 URL（运行时动态切换）
 *
 * @param url 日志服务基础 URL，空字符串表示使用后端 API
 *
 * @example
 * // 切换到独立日志服务器
 * setLogServiceUrl('https://logs.example.com');
 *
 * // 切换回后端 API
 * setLogServiceUrl('');
 */
export function setLogServiceUrl(url: string): void {
  currentConfig.baseUrl = url;
  // eslint-disable-next-line no-console
  console.info('[LogService] 日志服务 URL 已更新:', url || '(使用后端 API)');
}

/**
 * 从远程配置更新日志服务 URL
 *
 * 可以在获取监控配置时调用此函数
 */
export function updateLogServiceFromRemoteConfig(config: {
  logServiceUrl?: string;
}): void {
  if (config.logServiceUrl !== undefined) {
    setLogServiceUrl(config.logServiceUrl);
  }
}

/**
 * 获取当前日志服务配置
 */
export function getLogServiceConfig(): Readonly<LogServiceConfig> {
  return { ...currentConfig };
}

// ========================================================================
// URL 构建
// ========================================================================

/**
 * 构建完整的日志 API URL
 *
 * @param endpoint API 端点路径
 * @returns 完整 URL
 */
function buildUrl(endpoint: string): string {
  if (currentConfig.baseUrl) {
    // 使用独立日志服务器
    return `${currentConfig.baseUrl}${endpoint}`;
  }
  // 同源部署使用相对路径；绝对 API 配置时解析为 API 主机地址。
  return resolveApiUrl(endpoint);
}

/**
 * 获取错误日志上传 URL
 */
export function getErrorLogUrl(): string {
  return buildUrl(currentConfig.errorEndpoint);
}

/**
 * 获取性能日志上传 URL
 */
export function getPerformanceLogUrl(): string {
  return buildUrl(currentConfig.performanceEndpoint);
}

/**
 * 获取行为日志上传 URL
 */
export function getBehaviorLogUrl(): string {
  return buildUrl(currentConfig.behaviorEndpoint);
}

// ========================================================================
// 发送日志
// ========================================================================

/**
 * 读取当前登录用户的 Bearer Token（未登录或 store 未就绪返回 null）。
 *
 * 后端公开上传接口只在检测到 Authorization 头时才绑定真实用户，否则一律按
 * 匿名落库——行为上报若不带 Token，已登录用户的操作记录会全部丢失身份
 * （2026-10-04 审查 EWB-20）。凭据始终由服务端从 Token 解出，客户端自报的
 * user_id 会被覆盖，不存在冒充身份面。
 */
function getBearerToken(): null | string {
  try {
    const token = useAccessStore().accessToken;
    return token || null;
  } catch {
    return null;
  }
}

function buildAuthHeaders(): Record<string, string> {
  const token = getBearerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * 发送日志到日志服务
 *
 * 统一的日志发送函数，处理：
 * - URL 构建
 * - 跨域请求（独立服务器时需要）
 * - 错误处理
 *
 * @param type 日志类型
 * @param logs 日志数据
 * @returns 响应数据或 null（失败时）
 */
export async function sendLogs(
  type: 'behavior' | 'error' | 'performance',
  logs: any[],
): Promise<any | null> {
  if (logs.length === 0) {
    return null;
  }

  const urlMap = {
    error: getErrorLogUrl(),
    performance: getPerformanceLogUrl(),
    behavior: getBehaviorLogUrl(),
  };

  const url = urlMap[type];

  if (!canSendLogRequest()) {
    return null;
  }

  try {
    const deviceId = await getDeviceId().catch(() => null);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(deviceId ? { 'X-Device-ID': deviceId } : {}),
        ...buildAuthHeaders(),
      },
      // 跨域时需要携带凭证（如果日志服务需要认证）
      credentials: currentConfig.baseUrl ? 'include' : 'same-origin',
      body: JSON.stringify({ logs }),
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status === 429) {
      markLogRequestLimited(response.headers.get('Retry-After'));
    }

    return null;
  } catch {
    // 上报失败，静默处理
    return null;
  }
}

/**
 * 页面卸载时的可靠上报：fetch keepalive 支持自定义 Authorization 头
 * （sendBeacon 无法携带认证头，卸载批此前只能匿名落库）。
 * keepalive 载荷上限 64KB，超出或浏览器不支持时回退 sendBeacon（匿名）。
 */
export async function sendLogsKeepalive(
  type: 'behavior' | 'error' | 'performance',
  logs: any[],
): Promise<boolean> {
  if (logs.length === 0) {
    return true;
  }

  if (!canSendLogRequest()) {
    return false;
  }

  const urlMap = {
    error: getErrorLogUrl(),
    performance: getPerformanceLogUrl(),
    behavior: getBehaviorLogUrl(),
  };

  try {
    const deviceId = await getDeviceId().catch(() => null);
    const response = await fetch(urlMap[type], {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(deviceId ? { 'X-Device-ID': deviceId } : {}),
        ...buildAuthHeaders(),
      },
      body: JSON.stringify({ logs }),
    });
    return response.ok;
  } catch {
    // keepalive 失败回退 sendBeacon（匿名，至少保数据）
    return sendLogsBeacon(type, logs);
  }
}

/**
 * 使用 sendBeacon 发送日志（keepalive 不可用时的兜底）
 *
 * @param type 日志类型
 * @param logs 日志数据
 */
export function sendLogsBeacon(
  type: 'behavior' | 'error' | 'performance',
  logs: any[],
): boolean {
  if (logs.length === 0) {
    return true;
  }

  const urlMap = {
    error: getErrorLogUrl(),
    performance: getPerformanceLogUrl(),
    behavior: getBehaviorLogUrl(),
  };

  const url = urlMap[type];
  const payload = JSON.stringify({ logs });

  if (!canSendLogRequest()) {
    return false;
  }

  const body = new Blob([payload], { type: 'application/json' });
  return navigator.sendBeacon(url, body);
}

// 自动初始化
initLogServiceConfig();
